import { API_BASE_URL } from '../api';
import { authService } from './authService';
import type { FaqCategory, FaqItem } from '../types/faq';

/** FAQ publique : uniquement les questions publiées. */
export async function getPublicFaq(): Promise<FaqCategory[]> {
  const response = await fetch(`${API_BASE_URL}/api/faq`);
  if (!response.ok) {
    throw new Error('Erreur lors du chargement de la FAQ');
  }
  return response.json();
}

async function backofficeFetch(path: string, init?: RequestInit) {
  const headers = await authService.getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/api/backoffice/faq${path}`, {
    ...init,
    headers: { ...headers, ...(init?.headers || {}) },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Erreur lors de la mise à jour de la FAQ' }));
    throw new Error(error.detail || 'Erreur lors de la mise à jour de la FAQ');
  }

  return response.json();
}

export const faqService = {
  /** FAQ complète pour le back office, questions masquées incluses. */
  async list(): Promise<FaqCategory[]> {
    return backofficeFetch('');
  },

  async createCategory(title: string): Promise<FaqCategory> {
    return backofficeFetch('/categories', {
      method: 'POST',
      body: JSON.stringify({ title }),
    });
  },

  async renameCategory(categoryId: number, title: string): Promise<FaqCategory> {
    return backofficeFetch(`/categories/${categoryId}`, {
      method: 'PUT',
      body: JSON.stringify({ title }),
    });
  },

  async deleteCategory(categoryId: number): Promise<{ deleted_items: number }> {
    return backofficeFetch(`/categories/${categoryId}`, { method: 'DELETE' });
  },

  async moveCategory(categoryId: number, direction: 'up' | 'down'): Promise<{ moved: boolean }> {
    return backofficeFetch(`/categories/${categoryId}/move?direction=${direction}`, { method: 'PUT' });
  },

  async createItem(payload: {
    category_id: number;
    question: string;
    answer: string;
    tags: string[];
    is_published: boolean;
  }): Promise<FaqItem> {
    return backofficeFetch('/items', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  async updateItem(itemId: number, payload: {
    category_id?: number;
    question?: string;
    answer?: string;
    tags?: string[];
    is_published?: boolean;
  }): Promise<FaqItem> {
    return backofficeFetch(`/items/${itemId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },

  async deleteItem(itemId: number): Promise<{ success: boolean }> {
    return backofficeFetch(`/items/${itemId}`, { method: 'DELETE' });
  },

  async moveItem(itemId: number, direction: 'up' | 'down'): Promise<{ moved: boolean }> {
    return backofficeFetch(`/items/${itemId}/move?direction=${direction}`, { method: 'PUT' });
  },
};
